import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useState } from "react";
import { toast } from "sonner";
import api, { errMsg, fileUrl } from "@/lib/api";
import useFetch from "@/lib/useFetch";
import { Loading, ErrorState, Avatar, EmptyState } from "@/components/common";
import Voltar from "@/components/Voltar";
import { brl, fmtDate, fmtDateTime, stageColor } from "@/lib/format";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useAuth } from "@/context/AuthContext";
import OrientacaoThread from "@/components/OrientacaoThread";
import AtividadeReacoes from "@/components/AtividadeReacoes";
import CobrarWhatsApp from "@/components/CobrarWhatsApp";
import RegistrarAtividade from "@/components/RegistrarAtividade";
import AmpliarMixModal from "@/components/AmpliarMixModal";
import Dica from "@/components/Dica";
import { DICAS } from "@/lib/dicas";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { ArrowLeft, Pencil, Trash2, Phone, Mail, MapPin, Building2, Plus, Calculator, ShieldX, Loader2, MoreHorizontal, MessageCircle, KanbanSquare, CalendarClock, ClipboardList, Video, Trophy, XCircle, Undo2 } from "@/components/icons";
import { telHref, waHref, mailHref, mapsHref } from "@/lib/contactActions";
import { extractDateFromText, isSameLocalDay } from "@/lib/dateFromText";
import ReagendarProximaDialog from "@/components/ReagendarProximaDialog";
import DivergenciaDataDialog from "@/components/DivergenciaDataDialog";
import CanalClienteFicha from "@/components/CanalClienteFicha";

const NEW_ATIV_INPUT = "w-full h-11 px-3.5 rounded-lg border border-input bg-white text-sm outline-none focus:ring-2 focus:ring-brand/40";

// Helpers p/ preencher `datetime-local` input.
const pad = (n) => String(n).padStart(2, "0");
function toLocalInput(d) {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
function nowLocalInput() {
  return toLocalInput(new Date());
}
function nextBusinessDayAt9Local() {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  while (d.getDay() === 0 || d.getDay() === 6) d.setDate(d.getDate() + 1);
  d.setHours(9, 0, 0, 0);
  return toLocalInput(d);
}
function startOfTodayMs() {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}

const CANAIS = [
  { k: "Ligação", label: "Ligação", Icon: Phone },
  { k: "WhatsApp", label: "WhatsApp", Icon: MessageCircle },
  { k: "E-mail", label: "E-mail", Icon: Mail },
  { k: "Videochamada", label: "Videochamada", Icon: Video },
];
const TIPOS_PROX = ["Ligação", "Visita", "Reunião", "E-mail", "WhatsApp", "Fechamento", "Próximo contato"];

function Stat({ label, value }) {
  return (
    <div className="bg-slate-50 rounded-xl p-4">
      <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="text-lg font-semibold text-navy mt-1">{value}</div>
    </div>
  );
}

export default function ClienteDetalhe() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [sp, setSp] = useSearchParams();
  const cobrancaHighlightId = sp.get("cobranca") || "";
  const { data, loading, error, reload } = useFetch(`/clientes/${id}/resumo`);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectMotivo, setRejectMotivo] = useState("");
  const [rejectBusy, setRejectBusy] = useState(false);
  // Item 1.7 — exclusão de cliente protegida atrás de "…", oculta para vendedor
  // e exige digitar o nome exato.
  const [adminDelMotivo, setAdminDelMotivo] = useState("");
  const [adminDelBusy, setAdminDelBusy] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteConfirmName, setDeleteConfirmName] = useState("");

  const managerish = ["admin", "director", "manager", "diretor_geral"].includes(user?.role);
  const canDelete = user?.role === "admin";

  // PROSP-1 — encerrar / excluir prospect (hierarquia: scoped_by_id valida no backend).
  const [encerrarOpen, setEncerrarOpen] = useState(false);
  const [encerrarMotivo, setEncerrarMotivo] = useState("cliente_desistiu");
  const [encerrarMotivoTexto, setEncerrarMotivoTexto] = useState("");
  const [encerrarBusy, setEncerrarBusy] = useState(false);
  const [reativarBusy, setReativarBusy] = useState(false);
  const [excluirProspOpen, setExcluirProspOpen] = useState(false);
  const [excluirProspTexto, setExcluirProspTexto] = useState("");
  const [excluirProspBusy, setExcluirProspBusy] = useState(false);

  // Aviso "segure um botão para ver o que faz" — só no celular e uma única vez.
  const [hintDismissed, setHintDismissed] = useState(() => {
    try { return localStorage.getItem("mpires_dica_hint_v1") === "1"; } catch { return true; }
  });
  const dismissHint = () => {
    try { localStorage.setItem("mpires_dica_hint_v1", "1"); } catch (e) { void e; }
    setHintDismissed(true);
  };

  // Barra de ações da ficha — três formulários com regras distintas.
  //  - modo "visita" e "contato" gravam algo QUE JÁ ACONTECEU (concluida=true),
  //    data padrão = agora, data futura não é aceita, "Agendar próxima ação"
  //    opcional cria uma 2ª atividade PENDENTE no próximo dia útil às 9h.
  //  - modo "agendar" grava a próxima ação (pendente), data padrão = próximo
  //    dia útil às 9h, sem aceitar data passada.
  //  - "contato" mostra os canais (Ligação · WhatsApp · E-mail · Videochamada);
  //    o canal escolhido vira o `tipo` da atividade registrada.
  const [ativOpen, setAtivOpen] = useState(false);
  const [registrarOpen, setRegistrarOpen] = useState(false);
  const [ampliarOpen, setAmpliarOpen] = useState(false);
  const [ativMode, setAtivMode] = useState("visita"); // "visita" | "contato" | "agendar"
  const [ativCanal, setAtivCanal] = useState("Ligação");
  const [ativTitulo, setAtivTitulo] = useState("");
  const [ativData, setAtivData] = useState("");
  const [ativObs, setAtivObs] = useState("");
  const [ativBusy, setAtivBusy] = useState(false);
  const [ativAgendarProx, setAtivAgendarProx] = useState(false);
  const [ativProxTipo, setAtivProxTipo] = useState("Próximo contato");
  const [ativProxData, setAtivProxData] = useState("");
  const [ativProxTitulo, setAtivProxTitulo] = useState("");

  // Reagendar/visualizar "Próxima ação" (igual à oportunidade).
  const [reagOpen, setReagOpen] = useState(false);
  const [reagData, setReagData] = useState("");
  const [reagDiverg, setReagDiverg] = useState(null); // {texto, campo}
  const [gestorNaOpen, setGestorNaOpen] = useState(false);

  const abrirVisita = () => {
    setAtivMode("visita");
    setAtivTitulo(`Visita — ${data?.cliente?.empresa || data?.cliente?.nome || ""}`);
    setAtivData(nowLocalInput());
    setAtivObs("");
    setAtivAgendarProx(false);
    setAtivProxTipo("Próximo contato");
    setAtivProxData(nextBusinessDayAt9Local());
    setAtivProxTitulo("");
    setAtivOpen(true);
  };
  const abrirContato = () => {
    setAtivMode("contato");
    setAtivCanal("Ligação");
    setAtivTitulo(`Contato — ${data?.cliente?.empresa || data?.cliente?.nome || ""}`);
    setAtivData(nowLocalInput());
    setAtivObs("");
    setAtivAgendarProx(false);
    setAtivProxTipo("Próximo contato");
    setAtivProxData(nextBusinessDayAt9Local());
    setAtivProxTitulo("");
    setAtivOpen(true);
  };
  const abrirAgendar = () => {
    setAtivMode("agendar");
    setAtivTitulo(`Próxima ação — ${data?.cliente?.empresa || data?.cliente?.nome || ""}`);
    setAtivData(nextBusinessDayAt9Local());
    setAtivObs("");
    setAtivOpen(true);
  };

  const criarAtividade = async (e) => {
    e.preventDefault();
    if (!ativTitulo.trim()) { toast.error("Informe um título"); return; }
    if (!ativData) { toast.error("Informe a data"); return; }
    const dtAtiv = new Date(ativData);
    if (Number.isNaN(dtAtiv.getTime())) { toast.error("Data inválida"); return; }
    const nowMs = Date.now();

    if (ativMode === "visita" || ativMode === "contato") {
      // Não aceita futuro — aviso claro conforme spec.
      if (dtAtiv.getTime() > nowMs + 60 * 1000) {
        toast.error("Para algo futuro, use Agendar próxima ação");
        return;
      }
      if (ativAgendarProx) {
        if (!ativProxData) { toast.error("Informe a data da próxima ação"); return; }
        const dtProx = new Date(ativProxData);
        if (Number.isNaN(dtProx.getTime()) || dtProx.getTime() < startOfTodayMs()) {
          toast.error("A próxima ação não pode ficar no passado");
          return;
        }
      }
    } else if (ativMode === "agendar") {
      if (dtAtiv.getTime() < startOfTodayMs()) {
        toast.error("A próxima ação não pode ficar no passado");
        return;
      }
    }

    setAtivBusy(true);
    try {
      const cliente = data?.cliente;
      const clienteRef = {
        cliente_id: cliente?.id,
        cliente_nome: cliente?.nome || cliente?.empresa || "",
      };
      if (ativMode === "visita") {
        await api.post("/atividades", {
          titulo: ativTitulo.trim(),
          tipo: "Visita",
          data: dtAtiv.toISOString(),
          concluida: true,
          descricao: ativObs.trim(),
          ...clienteRef,
        });
      } else if (ativMode === "contato") {
        await api.post("/atividades", {
          titulo: ativTitulo.trim(),
          tipo: ativCanal,
          data: dtAtiv.toISOString(),
          concluida: true,
          descricao: ativObs.trim(),
          ...clienteRef,
        });
      } else if (ativMode === "agendar") {
        await api.post("/atividades", {
          titulo: ativTitulo.trim(),
          tipo: "Próximo contato",
          data: dtAtiv.toISOString(),
          concluida: false,
          descricao: ativObs.trim(),
          ...clienteRef,
        });
      }
      if ((ativMode === "visita" || ativMode === "contato") && ativAgendarProx) {
        const dtProx = new Date(ativProxData);
        const proxTitulo = ativProxTitulo.trim() || `Próxima ação — ${cliente?.empresa || cliente?.nome || ""}`;
        await api.post("/atividades", {
          titulo: proxTitulo,
          tipo: ativProxTipo || "Próximo contato",
          data: dtProx.toISOString(),
          concluida: false,
          descricao: "",
          ...clienteRef,
        });
        toast.success("Atividades registradas");
      } else {
        toast.success(ativMode === "agendar" ? "Próxima ação agendada" : "Atividade registrada");
      }
      setAtivOpen(false);
      reload();
    } catch (err) { toast.error(errMsg(err)); }
    finally { setAtivBusy(false); }
  };

  const rejectar = async () => {
    const t = rejectMotivo.trim();
    if (t.length < 3) return toast.error("Motivo obrigatório");
    setRejectBusy(true);
    try {
      const { data: r } = await api.post(`/clientes/${id}/rejeitar`, { motivo: t });
      toast.success(r?.email_status === "smtp_sent" ? "Cadastro rejeitado — vendedor notificado por sino + e-mail" : "Cadastro rejeitado — vendedor notificado no sino");
      setRejectOpen(false);
      setRejectMotivo("");
      reload();
    } catch (err) { toast.error(errMsg(err)); }
    finally { setRejectBusy(false); }
  };

  const del = async () => {
    if (adminDelMotivo.trim().length < 3) return;
    setAdminDelBusy(true);
    try {
      await api.delete(`/admin/clientes/${id}`, { params: { motivo: adminDelMotivo.trim() } });
      toast.success("Cliente movido para a lixeira");
      navigate("/clientes");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setAdminDelBusy(false);
    }
  };

  // PROSP-1 — encerrar / reativar / excluir prospect
  const confirmarEncerrar = async () => {
    if (encerrarMotivo === "outro" && encerrarMotivoTexto.trim().length < 3) {
      toast.error("Descreva o motivo");
      return;
    }
    setEncerrarBusy(true);
    try {
      await api.post(`/clientes/${id}/encerrar`, {
        motivo: encerrarMotivo,
        motivo_texto: encerrarMotivo === "outro" ? encerrarMotivoTexto.trim() : "",
      });
      toast.success("Prospect encerrado");
      setEncerrarOpen(false);
      setEncerrarMotivoTexto("");
      reload();
    } catch (err) { toast.error(errMsg(err)); }
    finally { setEncerrarBusy(false); }
  };
  const reativarProspect = async () => {
    setReativarBusy(true);
    try {
      await api.post(`/clientes/${id}/reativar`);
      toast.success("Prospect reativado");
      reload();
    } catch (err) { toast.error(errMsg(err)); }
    finally { setReativarBusy(false); }
  };
  const excluirProspect = async () => {
    if (excluirProspTexto.trim().toLowerCase() !== "excluir definitivamente?") return;
    setExcluirProspBusy(true);
    try {
      await api.delete(`/clientes/${id}/excluir`);
      toast.success("Prospect enviado para a lixeira (30 dias)");
      navigate("/clientes");
    } catch (err) { toast.error(errMsg(err)); }
    finally { setExcluirProspBusy(false); }
  };

  const openCustoPorUso = () => {
    const enc = encodeURIComponent;
    const c = data?.cliente || {};
    // Usa o usuário já carregado em memória pelo AuthContext (evita
    // chamada assíncrona que faria o mobile bloquear o window.open como popup).
    const u = user || {};
    const qs = [
      ["clienteId", id || ""],
      ["cliente", c.nome || ""],
      ["segmento", c.segmento || ""],
      ["porte", c.porte || ""],
      ["vendedorId", u.id || ""],
      ["vendedor", u.name || ""],
      ["cargo", u.cargo || u.role || ""],
      ["fone", u.phone || ""],
      ["email", u.email || ""],
    ].map(([k, v]) => `${k}=${enc(v)}`).join("&");
    window.open(`/custo-por-uso?${qs}`, "_blank", "noopener");
  };

  // Abre o modal Reagendar com a data atual da próxima ação pré-preenchida.
  // Mesmo contrato da ficha da oportunidade (DealDetalhe).
  const abrirReagendar = (na) => {
    if (!na?.data) return;
    setReagData(toLocalInput(new Date(na.data)));
    setReagOpen(true);
  };
  const _executarReagendarAtiv = async (naId, novaData) => {
    try {
      await api.patch(`/atividades/${naId}`, { data: novaData.toISOString() });
      toast.success("Próxima ação reagendada");
      setReagOpen(false); setReagDiverg(null);
      reload();
    } catch (err) { toast.error(errMsg(err)); }
  };
  const reagendarProximaAcao = async (na) => {
    if (!na?.id || !reagData) return;
    const textoAtiv = `${na.descricao || ""} ${na.titulo || ""}`;
    const dTexto = extractDateFromText(textoAtiv);
    const dCampo = new Date(reagData);
    if (dTexto && !isSameLocalDay(dTexto, dCampo)) {
      setReagDiverg({ texto: dTexto, campo: dCampo, naId: na.id });
      return;
    }
    await _executarReagendarAtiv(na.id, dCampo);
  };

  if (loading) return <Loading />;
  if (error) return <ErrorState onRetry={reload} message="Cliente não encontrado ou fora do seu escopo." />;

  const { cliente, oportunidades = [], atividades = [], fotos = [], totais = {}, ultima_interacao, proxima_atividade, dono, cobrancas_wpp = [], ganho_info, perdido_info } = data;
  // PROSP-1 — estados do ciclo de vida (encerrar/excluir).
  const isEncerrado = !!cliente.encerrado_em;
  const isExcluido = !!cliente.excluido_em;
  const temFaturamento = cliente.status === "Ativo" || !!cliente.ja_cliente_mpires;
  // Mostrar "Encerrar prospect": só para prospects (não cliente Ativo, não encerrado, não na lixeira).
  const podeEncerrar = !temFaturamento && !isEncerrado && !isExcluido;
  // Mostrar "Reativar": no estado encerrado.
  const podeReativar = isEncerrado && !isExcluido;
  // Mostrar "Excluir prospect": só sem faturamento Sankhya e não já na lixeira.
  const podeExcluirProspect = !temFaturamento && !isExcluido;
  // Motivos (mesma lista do backend).
  const MOTIVOS_ENCERRAR = [
    { k: "cliente_desistiu", label: "Cliente desistiu" },
    { k: "preco", label: "Preço" },
    { k: "concorrente", label: "Ficou com concorrente" },
    { k: "sem_retorno", label: "Sem retorno" },
    { k: "outro", label: "Outro" },
  ];
  // "Dias sem contato" usa a data da ATIVIDADE (`ultima_interacao.data`),
  // não a data em que foi concluída/salva no sistema.
  const diasSemAtividade = ultima_interacao?.data || ultima_interacao?.concluida_em
    ? Math.max(0, Math.floor((Date.now() - new Date(ultima_interacao.data || ultima_interacao.concluida_em).getTime()) / 86400000))
    : null;
  // Motivo automático para a mensagem de cobrança: sem contato há Xd, ou
  // tarefa atrasada (próxima atividade com data passada e não concluída).
  const _proxAtrasada = proxima_atividade && proxima_atividade.data && new Date(proxima_atividade.data).getTime() < Date.now();
  const cobrarMotivo = diasSemAtividade != null && diasSemAtividade >= 7
    ? `sem contato há ${diasSemAtividade} dias`
    : _proxAtrasada ? "com tarefa atrasada" : null;

  // Pedido CORRECAO_PROXIMA_ACAO_CLIENTE_AGENDA: a célula "Próxima ação"
  // vira clicável — vendedor dono reagenda; gestor abre read-only.
  const naOverdue = _proxAtrasada;
  const isOwnerPA = proxima_atividade && user?.id && (proxima_atividade.owner_id === user.id || cliente.owner_id === user.id);
  const paClickable = !!proxima_atividade && (managerish || isOwnerPA);

  return (
    <div className="pb-24 md:pb-0">
      {!hintDismissed && (
        <div
          className="md:hidden fixed left-3 right-3 z-40 rounded-lg bg-navy text-white shadow-lg px-3 py-2 text-xs flex items-center gap-3"
          style={{ bottom: "calc(9rem + env(safe-area-inset-bottom))" }}
          data-testid="dica-hint-banner"
        >
          <span className="flex-1">Dica: segure um botão para ver o que ele faz</span>
          <button
            onClick={dismissHint}
            data-testid="dica-hint-ok"
            className="text-xs font-semibold bg-white/20 hover:bg-white/30 rounded px-2.5 py-1"
          >
            Entendi
          </button>
        </div>
      )}
      <Voltar fallback="/clientes" label="Clientes" className="mb-4" />

      {cobrancaHighlightId && (() => {
        const c = cobrancas_wpp.find((x) => x.id === cobrancaHighlightId);
        if (!c) return null;
        const [y, m, d] = String(c.prazo || "").split("-");
        const prazoStr = y ? `${d}/${m}/${y}` : c.prazo;
        return (
          <button
            onClick={() => navigate(`/solicitacao/${c.id}`)}
            className="w-full text-left mb-4 rounded-xl border border-green-600/30 bg-green-50 hover:bg-green-100 p-4 flex items-start gap-3 transition-colors"
            data-testid="cobranca-destaque"
          >
            <MessageCircle className="w-5 h-5 text-green-700 mt-0.5 shrink-0" />
            <div className="flex-1 text-sm">
              <div className="font-semibold text-green-800">
                Solicitação de {c.manager_name} · enviada por WhatsApp
              </div>
              <div className="text-slate-700 mt-1">{c.text}</div>
              <div className="text-xs text-slate-500 mt-1">
                Prazo: {prazoStr} · Registrada em {fmtDateTime(c.created_at)} · Toque para abrir a conversa
              </div>
            </div>
          </button>
        );
      })()}

      {/* PROSP-1 — banner "Prospect encerrado" */}
      {isEncerrado && !isExcluido && (
        <div className="mb-4 rounded-xl border border-slate-300 bg-slate-100 p-4 flex items-start gap-3" data-testid="prospect-encerrado-banner">
          <XCircle className="w-5 h-5 text-slate-600 mt-0.5 shrink-0" />
          <div className="flex-1 text-sm">
            <div className="font-semibold text-slate-800">Prospect encerrado por {cliente.encerrado_por_nome || "—"}</div>
            <div className="text-slate-700 mt-1"><b>Motivo:</b> {cliente.encerrado_motivo_label || cliente.encerrado_motivo || "—"}</div>
            <div className="text-xs text-slate-500 mt-1">
              {cliente.encerrado_em && `Encerrado em ${fmtDateTime(cliente.encerrado_em)}`} · fora do funil e das listas ativas
            </div>
          </div>
          {podeReativar && (
            <button
              onClick={reativarProspect}
              disabled={reativarBusy}
              data-testid="reativar-prospect-btn"
              className="shrink-0 h-9 px-3 rounded-lg bg-brand text-white text-xs font-semibold hover:bg-brand-hover disabled:opacity-60 flex items-center gap-1.5"
            >
              {reativarBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Undo2 className="w-3.5 h-3.5" />} Reativar
            </button>
          )}
        </div>
      )}
      {/* PROSP-1 — banner "Na lixeira" */}
      {isExcluido && (
        <div className="mb-4 rounded-xl border border-bad bg-bad-soft p-4 flex items-start gap-3" data-testid="prospect-lixeira-banner">
          <Trash2 className="w-5 h-5 text-bad mt-0.5 shrink-0" />
          <div className="flex-1 text-sm">
            <div className="font-semibold text-bad">Cliente na lixeira</div>
            <div className="text-xs text-slate-600 mt-1">
              {cliente.excluido_em && `Excluído em ${fmtDateTime(cliente.excluido_em)}`}
              {cliente.excluido_por_nome ? ` por ${cliente.excluido_por_nome}` : ""} · será mantido por 30 dias
            </div>
          </div>
        </div>
      )}
      {cliente.status === "Rejeitado" && (
        <div className="mb-4 rounded-xl border border-bad bg-bad-soft p-4 flex items-start gap-3" data-testid="cliente-rejeitado-banner">
          <ShieldX className="w-5 h-5 text-bad mt-0.5" />
          <div className="flex-1 text-sm">
            <div className="font-semibold text-bad">Cadastro rejeitado por {cliente.rejeitado_por_nome || "gestor"}</div>
            <div className="text-slate-700 mt-1"><b>Motivo:</b> {cliente.rejeicao_motivo}</div>
            <div className="text-xs text-slate-500 mt-1">
              {cliente.rejeitado_em && `Rejeitado em ${fmtDateTime(cliente.rejeitado_em)}`}
              {cliente.owner_id === user?.id && " · Salve uma edição para restaurar o status."}
            </div>
          </div>
        </div>
      )}

      {/* LOTE CG-1 G1 — faixa de CLIENTE CONQUISTADO */}
      {ganho_info && cliente.status === "Ativo" && (
        <div
          className="mb-4 rounded-xl bg-ok text-white px-4 py-3 flex items-start gap-3 w-full"
          data-testid="cliente-conquistado-banner"
        >
          <Trophy className="w-7 h-7 mt-0.5 shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="text-base sm:text-lg font-extrabold leading-tight">CLIENTE CONQUISTADO</div>
            <div className="text-xs sm:text-sm leading-snug break-words opacity-95">
              Ganho em {ganho_info.at ? fmtDate(ganho_info.at) : "—"}
              {ganho_info.deal_titulo ? ` · ${ganho_info.deal_titulo}` : ""}
              {Number(ganho_info.valor) ? ` · ${brl(ganho_info.valor)}` : ""}
              {ganho_info.owner_name ? ` · ${ganho_info.owner_name}` : ""}
            </div>
          </div>
        </div>
      )}

      {/* LOTE CG-1 G1 — faixa de OPORTUNIDADE PERDIDA */}
      {perdido_info && cliente.status !== "Ativo" && (
        <div
          className="mb-4 rounded-xl bg-slate-200 text-slate-800 px-4 py-3 flex items-start gap-3 w-full"
          data-testid="cliente-perdido-banner"
        >
          <ShieldX className="w-6 h-6 mt-0.5 shrink-0 text-slate-500" />
          <div className="flex-1 min-w-0">
            <div className="text-base sm:text-lg font-extrabold leading-tight">OPORTUNIDADE PERDIDA</div>
            <div className="text-xs sm:text-sm leading-snug break-words">
              Perdida em {perdido_info.at ? fmtDate(perdido_info.at) : "—"}
              {perdido_info.motivo ? ` · ${perdido_info.motivo}` : ""}
              {perdido_info.retorno_at ? ` · retorno em ${fmtDate(perdido_info.retorno_at)}` : ""}
            </div>
          </div>
          <button
            onClick={() => navigate(`/deal/novo?cliente_id=${cliente.id}&cliente_nome=${encodeURIComponent(cliente.nome || "")}`)}
            data-testid="cliente-nova-oportunidade-btn"
            className="shrink-0 h-9 px-3 rounded-lg bg-navy text-white text-xs font-semibold hover:bg-navy/90"
          >
            Nova oportunidade
          </button>
        </div>
      )}

      <div className="bg-card rounded-2xl border border-black/5 p-6 shadow-[0_4px_20px_rgb(0,0,0,0.03)]">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-4">
            <Avatar name={(cliente.nome_fantasia && cliente.nome_fantasia.trim()) || cliente.empresa || cliente.nome} src={cliente.avatar} size={56} />
            <div>
              <h1 className="text-2xl font-semibold text-navy flex items-center gap-1.5">
                <Building2 className="w-5 h-5 text-slate-500" />
                {(cliente.nome_fantasia && cliente.nome_fantasia.trim()) || cliente.empresa || cliente.nome}
              </h1>
              {(cliente.nome_fantasia && cliente.nome_fantasia.trim() && (cliente.razao_social || cliente.nome) && (cliente.razao_social || cliente.nome) !== cliente.nome_fantasia) ? (
                <p className="text-sm text-muted-foreground mt-0.5" data-testid="cliente-subtitulo">
                  {cliente.razao_social || cliente.nome}{cliente.segmento ? ` · ${cliente.segmento}` : ""}
                </p>
              ) : cliente.empresa ? (
                <p className="text-sm text-muted-foreground mt-0.5" data-testid="cliente-subtitulo">
                  {cliente.nome}{cliente.segmento ? ` · ${cliente.segmento}` : ""}
                </p>
              ) : cliente.segmento ? (
                <p className="text-sm text-muted-foreground mt-0.5" data-testid="cliente-subtitulo">{cliente.segmento}</p>
              ) : null}
              <div className="flex flex-wrap gap-3 mt-2 text-sm text-slate-600">
                {cliente.telefone && (
                  <span className="flex items-center gap-1.5">
                    <a href={telHref(cliente.telefone)} className="flex items-center gap-1.5 hover:text-brand" data-testid="cliente-detalhe-tel"><Phone className="w-3.5 h-3.5" />{cliente.telefone}</a>
                    <a href={waHref(cliente.telefone)} target="_blank" rel="noreferrer" data-testid="cliente-detalhe-wa" aria-label="WhatsApp" className="h-7 w-7 rounded-full bg-[#25D366]/10 flex items-center justify-center text-[#25D366] hover:bg-[#25D366]/20"><MessageCircle className="w-3.5 h-3.5" /></a>
                  </span>
                )}
                {cliente.email && <a href={mailHref(cliente.email)} data-testid="cliente-detalhe-mail" className="flex items-center gap-1.5 hover:text-brand"><Mail className="w-3.5 h-3.5" />{cliente.email}</a>}
                {cliente.cnpj && <span className="text-xs bg-slate-100 px-2 py-0.5 rounded">CNPJ {cliente.cnpj}</span>}
              </div>
              {cliente.endereco && (
                <p className="text-xs text-muted-foreground flex items-center gap-1.5 mt-1.5">
                  <MapPin className="w-3.5 h-3.5" />
                  <span>{cliente.endereco}</span>
                  <a
                    href={mapsHref({ logradouro: cliente.endereco, cidade: cliente.cidade, estado: cliente.estado, cep: cliente.cep })}
                    target="_blank"
                    rel="noreferrer"
                    data-testid="cliente-detalhe-maps"
                    className="text-brand font-medium hover:text-brand-hover underline-offset-2 hover:underline"
                  >
                    Como chegar
                  </a>
                </p>
              )}
            </div>
          </div>
          <div className="flex flex-wrap gap-2 w-full sm:w-auto" data-testid="cliente-actions">
            <Dica id="custo-uso" texto={DICAS.clienteCustoUso}>
              <button onClick={openCustoPorUso} data-testid="custo-por-uso-btn" className="flex items-center justify-center gap-1.5 h-10 px-3 rounded-lg border border-input text-sm font-medium hover:bg-slate-50 whitespace-nowrap shrink-0">
                <Calculator className="w-3.5 h-3.5 shrink-0" /> <span>Custo/uso</span>
              </button>
            </Dica>
            <button onClick={() => navigate(`/cliente/${id}/editar`)} data-testid="edit-cliente-btn" className="flex items-center justify-center gap-1.5 h-10 px-3 rounded-lg border border-input text-sm font-medium hover:bg-slate-50 whitespace-nowrap shrink-0">
              <Pencil className="w-3.5 h-3.5 shrink-0" /> <span>Editar</span>
            </button>
            {managerish && cliente.owner_id !== user?.id && cliente.status !== "Rejeitado" && cliente.status !== "Ativo" && (
              <button
                onClick={() => setRejectOpen(true)}
                data-testid="reject-cliente-btn"
                className="flex items-center justify-center gap-1.5 h-10 px-3 rounded-lg border border-bad/30 text-bad text-sm font-medium hover:bg-bad-soft whitespace-nowrap shrink-0"
              >
                <ShieldX className="w-3.5 h-3.5 shrink-0" /> <span>Rejeitar</span>
              </button>
            )}
            {/* PROSP-1 — Encerrar prospect (hierarquia validada no backend) */}
            {podeEncerrar && (
              <button
                onClick={() => { setEncerrarMotivo("cliente_desistiu"); setEncerrarMotivoTexto(""); setEncerrarOpen(true); }}
                data-testid="encerrar-prospect-btn"
                className="flex items-center justify-center gap-1.5 h-10 px-3 rounded-lg border border-slate-300 text-slate-700 text-sm font-medium hover:bg-slate-50 whitespace-nowrap shrink-0"
              >
                <XCircle className="w-3.5 h-3.5 shrink-0" /> <span>Encerrar prospect</span>
              </button>
            )}
            {/* PROSP-1 — Excluir prospect: só prospect sem faturamento Sankhya */}
            {podeExcluirProspect && (
              <button
                onClick={() => { setExcluirProspTexto(""); setExcluirProspOpen(true); }}
                data-testid="excluir-prospect-btn"
                className="flex items-center justify-center gap-1.5 h-10 px-3 rounded-lg border border-bad/30 text-bad text-sm font-medium hover:bg-bad-soft whitespace-nowrap shrink-0"
              >
                <Trash2 className="w-3.5 h-3.5 shrink-0" /> <span>Excluir prospect</span>
              </button>
            )}
            {canDelete && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    data-testid="cliente-more-btn"
                    aria-label="Mais opções"
                    className="flex items-center justify-center h-10 w-10 rounded-lg border border-input text-slate-600 hover:bg-slate-50"
                  >
                    <MoreHorizontal className="w-4 h-4" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem
                    onSelect={() => { setDeleteConfirmName(""); setAdminDelMotivo(""); setDeleteOpen(true); }}
                    data-testid="cliente-more-excluir"
                    className="text-bad focus:text-bad focus:bg-bad-soft"
                  >
                    <Trash2 className="w-4 h-4 mr-2" /> Excluir cliente (Admin)
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
            <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Excluir cliente (Admin)?</AlertDialogTitle>
                  <AlertDialogDescription>
                    O cliente e tudo ligado a ele (atividades, orientações, alertas,
                    cobranças, histórico) vão para a lixeira. Reversível pelo admin.
                    Cliente com oportunidade ligada não pode ser excluído.
                    Para confirmar, digite o nome exato: <b>{cliente.nome}</b>.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <input
                  value={deleteConfirmName}
                  onChange={(e) => setDeleteConfirmName(e.target.value)}
                  placeholder={cliente.nome}
                  data-testid="cliente-delete-confirm-input"
                  className="w-full h-11 px-3.5 rounded-lg border border-input bg-white text-sm outline-none focus:ring-2 focus:ring-brand/40"
                />
                <textarea
                  value={adminDelMotivo}
                  onChange={(e) => setAdminDelMotivo(e.target.value)}
                  rows={3}
                  minLength={3}
                  placeholder="Motivo (obrigatório, mín. 3 caracteres)"
                  data-testid="cliente-delete-motivo"
                  className="mt-2 w-full px-3.5 py-2.5 rounded-lg border border-input bg-white text-sm outline-none focus:ring-2 focus:ring-brand/40"
                />
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancelar</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={del}
                    disabled={adminDelBusy || deleteConfirmName.trim() !== (cliente.nome || "").trim() || adminDelMotivo.trim().length < 3}
                    className="bg-bad hover:bg-bad/90 disabled:opacity-50"
                    data-testid="confirm-delete-cliente"
                  >
                    Excluir
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-6">
          <Stat label="Oportunidades" value={totais.oportunidades || 0} />
          <Stat label="Em aberto" value={brl(totais.em_aberto_valor)} />
          <Stat label="Fechado" value={brl(totais.fechado_valor)} />
          <Stat label="Atividades pend." value={totais.atividades_pendentes || 0} />
        </div>
      </div>

      {/* Barra de ações da ficha — desktop: sticky logo abaixo do header;
          mobile: fixa acima da navegação inferior (h-16 em AppShell). */}
      <div
        data-testid="cliente-actionbar"
        className="mt-4 md:mt-4 md:sticky md:top-2 md:z-10 fixed md:relative bottom-16 md:bottom-auto left-0 right-0 md:left-auto md:right-auto z-30 bg-white md:bg-card md:rounded-2xl md:border md:border-black/5 md:shadow-[0_4px_20px_rgb(0,0,0,0.03)] border-t border-black/5 md:border-t-0 md:p-3 px-3 py-2"
      >
        <div className="grid grid-cols-2 md:flex md:flex-wrap md:items-center gap-2">
          <Dica id="acao-registrar-atividade" texto={DICAS.clienteRegistrarAtividade || "Registre visita, ligação, WhatsApp, e-mail ou videochamada — e o próximo passo."} className="w-full md:w-auto">
            <button
              data-testid="cliente-acao-registrar-atividade"
              onClick={() => setRegistrarOpen(true)}
              className="w-full flex flex-col md:flex-row items-center justify-center gap-1 md:gap-2 h-14 md:h-[38px] md:px-4 rounded-lg bg-brand text-white text-[11px] md:text-sm font-semibold hover:bg-brand-hover transition-colors"
            >
              <ClipboardList className="w-4 h-4" /> <span className="leading-tight">Registrar atividade</span>
            </button>
          </Dica>
          <Dica id="acao-oportunidade" texto={DICAS.clienteOportunidade} className="w-full md:w-auto">
            <button
              data-testid="cliente-acao-oportunidade"
              onClick={() => navigate(`/deal/novo?cliente_id=${cliente.id}&cliente_nome=${encodeURIComponent(cliente.nome)}`)}
              className="w-full flex flex-col md:flex-row items-center justify-center gap-1 md:gap-2 h-14 md:h-[38px] md:px-4 rounded-lg bg-white border border-[#D5DEE5] text-navy text-[11px] md:text-sm font-semibold hover:bg-slate-50 transition-colors"
            >
              <KanbanSquare className="w-4 h-4" style={{ color: "#62717F" }} /> <span className="leading-tight">Nova oportunidade</span>
            </button>
          </Dica>
          {/* LOTE X-1 F1: "Ampliar mix" — só para o dono do cliente, quando
              o cliente já é da MPires (status Ativo). Entra no Funil como
              oportunidade do tipo "ampliacao_mix". */}
          {cliente.status === "Ativo" && cliente.owner_id === user?.id && (
            <button
              data-testid="ampliar-mix-cliente"
              onClick={() => setAmpliarOpen(true)}
              className="w-full md:w-auto flex flex-col md:flex-row items-center justify-center gap-1 md:gap-2 h-14 md:h-[38px] md:px-4 rounded-lg bg-brand text-white text-[11px] md:text-sm font-semibold hover:bg-brand-hover transition-colors"
            >
              <Plus className="w-4 h-4" /> <span className="leading-tight">Ampliar mix</span>
            </button>
          )}
          <CanalClienteFicha cliente={cliente} slot="button" />
          {managerish && dono && dono.id !== user?.id && (
            <div className="col-span-2 md:col-auto w-full md:w-auto">
              <Dica id="acao-pedir-retorno" texto={DICAS.clientePedirRetorno} className="w-full md:w-auto">
                <CobrarWhatsApp
                  vendedor={{ id: dono.id, name: dono.name, phone: dono.phone }}
                  cliente={{ id: cliente.id, nome: cliente.empresa || cliente.nome }}
                  motivo={cobrarMotivo}
                  variant="ghost"
                  testId={`cliente-${cliente.id}`}
                />
              </Dica>
            </div>
          )}
        </div>
      </div>

      <div className="bg-card rounded-2xl border border-black/5 p-6 shadow-[0_4px_20px_rgb(0,0,0,0.03)] mt-4" data-testid="cliente-gestao">
        <div className="grid md:grid-cols-2 gap-x-8 gap-y-3 text-sm">
          <div className="flex justify-between gap-3"><span className="text-muted-foreground">Vendedor responsável</span><span className="font-medium text-navy">{cliente.owner_name || "—"}</span></div>
          <div className="flex justify-between gap-3"><span className="text-muted-foreground">Segmento</span><span className="font-medium text-navy" data-testid="cliente-segmento">{cliente.segmento || "Não informado"}</span></div>
          <div className="flex justify-between gap-3"><span className="text-muted-foreground">Situação</span><span className="font-medium text-navy" data-testid="cliente-situacao">{({ Potencial: "Prospect", Ativo: "Cliente", Inativo: "Cliente inativo" }[cliente.status] || cliente.status || "Cliente")}</span></div>
          <div className="flex justify-between gap-3"><span className="text-muted-foreground">Decisor</span><span className="font-medium text-navy">{cliente.decisor_nome || "—"}{cliente.decisor_cargo ? ` · ${cliente.decisor_cargo}` : ""}</span></div>
          <div className="flex justify-between gap-3"><span className="text-muted-foreground">Contato do decisor</span><span className="font-medium text-navy">{cliente.decisor_telefone || cliente.decisor_email || "—"}</span></div>
          <div className="flex justify-between gap-3"><span className="text-muted-foreground">Última atividade</span><span className="font-medium text-navy">{ultima_interacao ? `${ultima_interacao.tipo || "Atividade"} · ${fmtDate(ultima_interacao.data || ultima_interacao.concluida_em)}` : "—"}</span></div>
          {(() => {
            // LOTE CG-1 G1 — Cliente Ativo sem oportunidade aberta: vira
            // "Dias sem contato (pós-venda)" com cor neutra até 30d.
            const hasOpen = oportunidades.some((d) => d.stage !== "Fechado" && d.stage !== "Perdido");
            const posVenda = cliente.status === "Ativo" && !hasOpen;
            const label = posVenda ? "Dias sem contato (pós-venda)" : "Dias sem atividade";
            let cls = "text-navy";
            if (!posVenda && diasSemAtividade != null) {
              if (diasSemAtividade >= 15) cls = "text-bad";
              else if (diasSemAtividade >= 7) cls = "text-warn";
            } else if (posVenda && diasSemAtividade != null && diasSemAtividade >= 30) {
              cls = "text-warn";
            }
            return (
              <div className="flex justify-between gap-3">
                <span className="text-muted-foreground">{label}</span>
                <span className={`font-semibold ${cls}`} data-testid="dias-sem-contato">
                  {diasSemAtividade != null ? `${diasSemAtividade} dias` : "—"}
                </span>
              </div>
            );
          })()}
          <div className="flex justify-between gap-3">
            <span className="text-muted-foreground">Próxima ação</span>
            {proxima_atividade ? (
              <button
                type="button"
                onClick={() => {
                  if (!paClickable) return;
                  if (managerish && !isOwnerPA) { setGestorNaOpen(true); return; }
                  abrirReagendar(proxima_atividade);
                }}
                className={`font-medium ${naOverdue ? "text-bad" : "text-ok"} ${paClickable ? "underline-offset-2 hover:underline cursor-pointer" : "cursor-default"} text-left`}
                data-testid="proxima-acao"
              >
                {proxima_atividade.tipo || "Atividade"} · {fmtDate(proxima_atividade.data)}{naOverdue ? " (atrasada)" : ""}
              </button>
            ) : (
              <span className="font-semibold text-bad" data-testid="sem-proxima-acao">Sem próxima ação</span>
            )}
          </div>
          <div className="flex justify-between gap-3"><span className="text-muted-foreground">Oportunidades abertas</span><span className="font-medium text-navy">{oportunidades.filter((d) => d.stage !== "Fechado" && d.stage !== "Perdido").length}</span></div>
        </div>
      </div>

      <CanalClienteFicha cliente={cliente} slot="section" />

      <Tabs defaultValue="oportunidades" className="mt-6">
        <TabsList className="grid grid-cols-2 md:inline-flex w-full md:w-auto h-auto md:h-9 gap-1 md:gap-0 p-1">
          <TabsTrigger value="oportunidades" data-testid="tab-oportunidades" className="w-full md:w-auto">Oportunidades ({oportunidades.length})</TabsTrigger>
          <TabsTrigger value="atividades" data-testid="tab-atividades" className="w-full md:w-auto">Atividades ({atividades.length + cobrancas_wpp.length})</TabsTrigger>
          <TabsTrigger value="orientacoes" data-testid="tab-orientacoes" className="w-full md:w-auto">Orientações</TabsTrigger>
          <TabsTrigger value="fotos" data-testid="tab-fotos" className="w-full md:w-auto">Fotos ({fotos.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="oportunidades" className="mt-4">
          {oportunidades.length === 0 ? (
            <EmptyState
              icon={KanbanSquare}
              title="Nenhuma oportunidade"
              action={
                <button
                  data-testid="empty-oportunidade-btn"
                  onClick={() => navigate(`/deal/novo?cliente_id=${cliente.id}&cliente_nome=${encodeURIComponent(cliente.nome)}`)}
                  className="flex items-center gap-2 h-10 px-4 rounded-lg bg-brand text-white text-sm font-semibold hover:bg-brand-hover transition-colors"
                >
                  <Plus className="w-4 h-4" /> Nova oportunidade
                </button>
              }
            />
          ) : (
            <div className="space-y-2">
              {oportunidades.map((d) => (
                <div key={d.id} onClick={() => navigate(`/deal/${d.id}`)} className="bg-card rounded-xl border border-black/5 p-4 flex items-center justify-between cursor-pointer hover:bg-slate-50 transition-colors" data-testid={`deal-${d.id}`}>
                  <div>
                    <div className="font-medium text-navy">{d.titulo}</div>
                    <div className="text-xs text-muted-foreground">{brl(d.stage === "Fechado" && d.valor_fechado ? d.valor_fechado : d.valor)} · {d.prob}%</div>
                  </div>
                  <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${stageColor(d.stage)}`}>{d.stage === "Fechado" ? "Ganho" : d.stage}</span>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="atividades" className="mt-4">
          {atividades.length === 0 && cobrancas_wpp.length === 0 ? (
            <EmptyState
              icon={ClipboardList}
              title="Nenhuma atividade registrada"
              action={
                <button
                  data-testid="empty-atividade-btn"
                  onClick={() => setRegistrarOpen(true)}
                  className="flex items-center gap-2 h-10 px-4 rounded-lg bg-brand text-white text-sm font-semibold hover:bg-brand-hover transition-colors"
                >
                  <ClipboardList className="w-4 h-4" /> Registrar atividade
                </button>
              }
            />
          ) : (
            <div className="space-y-2">
              {[
                ...atividades.map((a) => ({ _kind: "ativ", _at: a.data, ...a })),
                ...cobrancas_wpp.map((c) => ({ _kind: "cobr", _at: c.created_at, ...c })),
              ]
                .sort((a, b) => (b._at || "").localeCompare(a._at || ""))
                .map((it) => it._kind === "cobr" ? (
                  <button key={`cobr-${it.id}`} onClick={() => navigate(`/solicitacao/${it.id}`)} className="w-full text-left bg-green-50 rounded-xl border border-green-600/20 hover:border-green-600/50 p-4 flex items-center justify-between gap-3 transition-colors" data-testid={`cobr-row-${it.id}`}>
                    <div className="min-w-0 flex items-start gap-3">
                      <MessageCircle className="w-4 h-4 text-green-700 mt-0.5 shrink-0" />
                      <div className="min-w-0">
                        <div className="font-medium text-navy">Solicitação de {it.manager_name} · enviada por WhatsApp</div>
                        <div className="text-xs text-slate-600 mt-0.5 line-clamp-2">{it.text}</div>
                        <div className="text-[11px] text-slate-500 mt-1">
                          {fmtDateTime(it.created_at)}{it.prazo ? ` · prazo ${it.prazo.split("-").reverse().join("/")}` : ""}
                          {it.respostas && it.respostas.length > 0 && (
                            <span className="ml-2 inline-flex items-center gap-1 text-green-700 font-semibold">· {it.respostas.length} resposta{it.respostas.length === 1 ? "" : "s"}</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </button>
                ) : (
                  <div key={it.id} className="bg-card rounded-xl border border-black/5 p-4 flex items-center justify-between gap-3 group relative" data-testid={`ativ-row-${it.id}`}>
                    <div className="min-w-0">
                      <div className="font-medium text-navy">{it.titulo}</div>
                      <div className="text-xs text-muted-foreground">{it.tipo} · {fmtDateTime(it.data)}</div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <AtividadeReacoes
                        atividade={it}
                        testId={`ativ-reac-${it.id}`}
                      />
                      <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${it.concluida ? "bg-ok-soft text-ok" : "bg-warn-soft text-warn"}`}>
                        {it.concluida ? "Concluída" : "Pendente"}
                      </span>
                    </div>
                  </div>
                ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="orientacoes" className="mt-4">
          <div className="bg-card rounded-2xl border border-black/5 p-5 shadow-[0_2px_10px_rgb(0,0,0,0.03)]">
            <OrientacaoThread clienteId={cliente.id} ownerId={cliente.owner_id} />
          </div>
        </TabsContent>

        <TabsContent value="fotos" className="mt-4">
          {fotos.length === 0 ? (
            <p className="text-sm text-muted-foreground py-6 text-center">Nenhuma foto anexada.</p>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {fotos.map((f) => (
                <div key={f.id} className="rounded-xl overflow-hidden border border-black/5 bg-slate-100 aspect-square">
                  <img src={fileUrl(f.id)} alt={f.categoria || "foto"} className="w-full h-full object-cover" loading="lazy" />
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      <Dialog open={ativOpen} onOpenChange={setAtivOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle data-testid="cliente-ativ-titulo-form">
              {ativMode === "visita" ? "O que aconteceu na visita?"
                : ativMode === "contato" ? "Como foi o contato?"
                : "Qual o próximo passo?"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={criarAtividade} className="space-y-3" data-testid="cliente-ativ-form">
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Cliente</label>
              <div
                data-testid="cliente-ativ-cliente-lock"
                className="mt-1 flex items-center gap-2 h-11 px-3.5 rounded-lg border border-input bg-slate-50 text-sm text-navy"
              >
                <Building2 className="w-4 h-4 text-slate-400" />
                <span className="truncate">{cliente.empresa || cliente.nome}</span>
              </div>
            </div>
            {ativMode === "contato" && (
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Canal</label>
                <div className="mt-1 grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="cliente-ativ-canal">
                  {CANAIS.map((c) => {
                    const active = ativCanal === c.k;
                    return (
                      <button
                        key={c.k}
                        type="button"
                        onClick={() => setAtivCanal(c.k)}
                        data-testid={`cliente-ativ-canal-${c.k}`}
                        className={`h-11 rounded-lg border text-sm font-medium flex items-center justify-center gap-1.5 transition-colors ${active ? "bg-brand text-white border-brand" : "border-input text-navy hover:bg-slate-50"}`}
                      >
                        <c.Icon className="w-4 h-4" /> {c.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
            <input
              required
              placeholder="Título"
              value={ativTitulo}
              onChange={(e) => setAtivTitulo(e.target.value)}
              className={NEW_ATIV_INPUT}
              data-testid="cliente-ativ-titulo"
            />
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                {ativMode === "agendar" ? "Quando?" : "Quando aconteceu?"}
              </label>
              <input
                type="datetime-local"
                value={ativData}
                onChange={(e) => setAtivData(e.target.value)}
                className={NEW_ATIV_INPUT + " mt-1"}
                data-testid="cliente-ativ-data"
              />
              {(ativMode === "visita" || ativMode === "contato") && (
                <p className="text-[11px] text-muted-foreground mt-1">Para algo futuro, use Agendar próxima ação.</p>
              )}
              {ativMode === "agendar" && (
                <p className="text-[11px] text-muted-foreground mt-1">Não pode ficar no passado.</p>
              )}
            </div>
            <textarea
              placeholder={ativMode === "visita" ? "O que foi observado (opcional)"
                : ativMode === "contato" ? "O que foi conversado (opcional)"
                : "Observação (opcional)"}
              value={ativObs}
              onChange={(e) => setAtivObs(e.target.value)}
              rows={3}
              className={`${NEW_ATIV_INPUT} h-auto py-2.5`}
              data-testid="cliente-ativ-obs"
            />
            {(ativMode === "visita" || ativMode === "contato") && (
              <div className="rounded-lg border border-black/5 bg-slate-50 p-3">
                <label className="flex items-center gap-2 text-sm text-navy cursor-pointer">
                  <input
                    type="checkbox"
                    checked={ativAgendarProx}
                    onChange={(e) => setAtivAgendarProx(e.target.checked)}
                    data-testid="cliente-ativ-agendar-prox"
                    className="w-4 h-4 accent-brand"
                  />
                  Agendar próxima ação
                </label>
                {ativAgendarProx && (
                  <div className="mt-3 space-y-2" data-testid="cliente-ativ-prox-fields">
                    <select
                      value={ativProxTipo}
                      onChange={(e) => setAtivProxTipo(e.target.value)}
                      className={NEW_ATIV_INPUT}
                      data-testid="cliente-ativ-prox-tipo"
                    >
                      {TIPOS_PROX.map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                    <input
                      type="datetime-local"
                      value={ativProxData}
                      onChange={(e) => setAtivProxData(e.target.value)}
                      className={NEW_ATIV_INPUT}
                      data-testid="cliente-ativ-prox-data"
                    />
                    <input
                      placeholder="Título da próxima ação (opcional)"
                      value={ativProxTitulo}
                      onChange={(e) => setAtivProxTitulo(e.target.value)}
                      className={NEW_ATIV_INPUT}
                      data-testid="cliente-ativ-prox-titulo"
                    />
                    <p className="text-[11px] text-muted-foreground">Padrão: próximo dia útil às 9h.</p>
                  </div>
                )}
              </div>
            )}
            <DialogFooter>
              <button
                type="submit"
                disabled={ativBusy}
                data-testid="cliente-ativ-save"
                className="h-10 px-5 rounded-lg bg-brand text-white text-sm font-semibold hover:bg-brand-hover flex items-center gap-2 disabled:opacity-60"
              >
                {ativBusy && <Loader2 className="w-4 h-4 animate-spin" />} Salvar
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <RegistrarAtividade
        open={registrarOpen}
        onOpenChange={setRegistrarOpen}
        cliente={cliente}
        onSaved={reload}
      />

      <AmpliarMixModal
        open={ampliarOpen}
        onOpenChange={setAmpliarOpen}
        cliente={{ id: cliente.id, nome: cliente.nome }}
      />

      {/* PROSP-1 — Modal: Encerrar prospect */}
      <Dialog open={encerrarOpen} onOpenChange={setEncerrarOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Encerrar prospect</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            <b>{cliente.nome}</b> sai do funil e das listas ativas, mas fica no histórico com motivo, data e quem encerrou.
            Pode ser reativado a qualquer momento (volta à etapa anterior).
          </p>
          <div className="space-y-2">
            <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Motivo</label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="encerrar-motivos">
              {MOTIVOS_ENCERRAR.map((m) => {
                const active = encerrarMotivo === m.k;
                return (
                  <button
                    key={m.k}
                    type="button"
                    onClick={() => setEncerrarMotivo(m.k)}
                    data-testid={`encerrar-motivo-${m.k}`}
                    className={`h-11 rounded-lg border text-sm font-medium transition-colors text-left px-3 ${active ? "bg-brand text-white border-brand" : "border-input text-navy hover:bg-slate-50"}`}
                  >
                    {m.label}
                  </button>
                );
              })}
            </div>
            {encerrarMotivo === "outro" && (
              <textarea
                value={encerrarMotivoTexto}
                onChange={(e) => setEncerrarMotivoTexto(e.target.value)}
                rows={3}
                minLength={3}
                placeholder="Descreva o motivo (obrigatório)"
                data-testid="encerrar-motivo-texto"
                className="w-full px-3.5 py-2.5 rounded-lg border border-input bg-white text-sm outline-none focus:ring-2 focus:ring-brand/40"
              />
            )}
          </div>
          <DialogFooter>
            <button
              onClick={() => setEncerrarOpen(false)}
              className="h-10 px-4 rounded-lg border border-input text-sm hover:bg-slate-50"
            >
              Cancelar
            </button>
            <button
              onClick={confirmarEncerrar}
              disabled={encerrarBusy || (encerrarMotivo === "outro" && encerrarMotivoTexto.trim().length < 3)}
              data-testid="encerrar-confirm"
              className="h-10 px-4 rounded-lg bg-brand text-white text-sm font-semibold hover:bg-brand-hover disabled:opacity-60 flex items-center gap-2"
            >
              {encerrarBusy && <Loader2 className="w-4 h-4 animate-spin" />} Encerrar prospect
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* PROSP-1 — Modal: Excluir prospect (soft delete 30 dias) */}
      <Dialog open={excluirProspOpen} onOpenChange={setExcluirProspOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Excluir prospect?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            O prospect <b>{cliente.nome}</b> vai para a lixeira por 30 dias.
            Gerente, diretor ou admin podem restaurar neste período.
            Para confirmar, digite exatamente: <b>Excluir definitivamente?</b>
          </p>
          <input
            value={excluirProspTexto}
            onChange={(e) => setExcluirProspTexto(e.target.value)}
            placeholder="Excluir definitivamente?"
            data-testid="excluir-prospect-input"
            className="w-full h-11 px-3.5 rounded-lg border border-input bg-white text-sm outline-none focus:ring-2 focus:ring-bad/40"
          />
          <DialogFooter>
            <button
              onClick={() => setExcluirProspOpen(false)}
              className="h-10 px-4 rounded-lg border border-input text-sm hover:bg-slate-50"
            >
              Cancelar
            </button>
            <button
              onClick={excluirProspect}
              disabled={excluirProspBusy || excluirProspTexto.trim().toLowerCase() !== "excluir definitivamente?"}
              data-testid="excluir-prospect-confirm"
              className="h-10 px-4 rounded-lg bg-bad text-white text-sm font-semibold hover:bg-bad/90 disabled:opacity-60 flex items-center gap-2"
            >
              {excluirProspBusy && <Loader2 className="w-4 h-4 animate-spin" />} Excluir
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={rejectOpen} onOpenChange={setRejectOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rejeitar cadastro do cliente</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            O cliente <b>{cliente.nome}</b> será marcado como <b>Rejeitado</b> e continuará existindo.
            O vendedor responsável receberá a rejeição no sino e por e-mail com o motivo.
            Ao editar o cadastro, o status volta ao anterior.
          </p>
          <textarea
            value={rejectMotivo}
            onChange={(e) => setRejectMotivo(e.target.value)}
            rows={4}
            placeholder="Ex.: Cliente sem CNPJ e sem segmento — favor completar o cadastro."
            data-testid="reject-motivo"
            className="w-full px-3.5 py-2.5 rounded-lg border border-input bg-white text-sm outline-none focus:ring-2 focus:ring-bad/40"
          />
          <DialogFooter>
            <button onClick={() => { setRejectOpen(false); setRejectMotivo(""); }} className="h-10 px-4 rounded-lg border border-input text-sm hover:bg-slate-50">Cancelar</button>
            <button
              onClick={rejectar}
              disabled={rejectBusy || rejectMotivo.trim().length < 3}
              data-testid="reject-confirm"
              className="h-10 px-4 rounded-lg bg-bad text-white text-sm font-semibold hover:bg-bad/90 disabled:opacity-60 flex items-center gap-2"
            >
              {rejectBusy && <Loader2 className="w-4 h-4 animate-spin" />} Rejeitar cadastro
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reagendar próxima ação (vendedor dono) — componente compartilhado */}
      <ReagendarProximaDialog
        open={reagOpen}
        onClose={() => setReagOpen(false)}
        value={reagData}
        onChange={setReagData}
        onSalvar={() => reagendarProximaAcao(proxima_atividade)}
        testId="cliente-reag"
      />

      {/* Divergência de data no Reagendar — componente compartilhado */}
      <DivergenciaDataDialog
        open={!!reagDiverg}
        onClose={() => setReagDiverg(null)}
        dataTexto={reagDiverg?.texto}
        dataCampo={reagDiverg?.campo}
        testId="cliente-reag-div-dialog"
        onManter={() => { const c = reagDiverg.campo; const naId = reagDiverg.naId; setReagDiverg(null); _executarReagendarAtiv(naId, c); }}
        onUsarTexto={() => {
          const base = reagDiverg.campo;
          const nova = new Date(
            reagDiverg.texto.getFullYear(), reagDiverg.texto.getMonth(), reagDiverg.texto.getDate(),
            base.getHours(), base.getMinutes(), 0, 0
          );
          const naId = reagDiverg.naId;
          setReagDiverg(null);
          _executarReagendarAtiv(naId, nova);
        }}
      />

      {/* Gestor: detalhes da próxima ação (read-only + Pedir retorno) */}
      <Dialog open={gestorNaOpen} onOpenChange={setGestorNaOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Próxima ação (visualização)</DialogTitle></DialogHeader>
          {proxima_atividade ? (
            <div className="space-y-3 text-sm" data-testid="cliente-gestor-na-detalhes">
              <div><span className="text-xs uppercase tracking-wider text-muted-foreground">Tipo</span><div className="font-medium text-navy">{proxima_atividade.tipo}</div></div>
              <div><span className="text-xs uppercase tracking-wider text-muted-foreground">Data e hora</span><div className={`font-medium ${naOverdue ? "text-bad" : "text-navy"}`}>{fmtDateTime(proxima_atividade.data)}{naOverdue ? " (atrasada)" : ""}</div></div>
              {proxima_atividade.titulo && proxima_atividade.titulo !== proxima_atividade.tipo && (
                <div><span className="text-xs uppercase tracking-wider text-muted-foreground">Título</span><div className="font-medium text-navy">{proxima_atividade.titulo}</div></div>
              )}
              {proxima_atividade.descricao && (
                <div><span className="text-xs uppercase tracking-wider text-muted-foreground">Descrição</span><div className="whitespace-pre-wrap text-navy">{proxima_atividade.descricao}</div></div>
              )}
              <div><span className="text-xs uppercase tracking-wider text-muted-foreground">Responsável</span><div className="font-medium text-navy">{cliente.owner_name || dono?.name || "—"}</div></div>
              {proxima_atividade.criada_em && (
                <div><span className="text-xs uppercase tracking-wider text-muted-foreground">Criada em</span><div className="text-slate-600">{fmtDateTime(proxima_atividade.criada_em)}</div></div>
              )}
              <p className="text-xs text-muted-foreground italic pt-2 border-t">Apenas o vendedor responsável conclui ou reagenda a atividade.</p>
            </div>
          ) : (
            <div className="text-sm text-muted-foreground">Sem próxima ação.</div>
          )}
          <DialogFooter>
            {dono?.id && (
              <CobrarWhatsApp
                vendedor={{ id: dono.id, name: dono.name, phone: dono.phone }}
                cliente={{ id: cliente.id, nome: cliente.empresa || cliente.nome }}
                motivo={cobrarMotivo}
                variant="primary"
                testId={`cliente-gestor-na-${cliente.id}`}
              />
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
